import type { Amount } from '@getstep/proto/common'
import { isPoints } from '@getstep/sdk/dist/http/models/Amount'
import {
    formatCryptoAmount,
    isKnownCryptoCurrency,
} from '@getstep/sdk/dist/http/models/Crypto'
import numeral from 'numeral'

export const {
    formatCurrency,
    formatCurrencyWithOptionalFraction,
    formatCurrencyParts,
    formatOptions,
    bitcoinToSatoshi,
    formatNumber,
    pointsToDollars,
} = new (class {
    private cache: Map<string, Intl.NumberFormat> = new Map()

    formatCurrency = (
        amount: Amount,
        options: FormatterOptions = {},
        locale?: string
    ): string => {
        const { currency } = amount

        if (isKnownCryptoCurrency(currency)) {
            return formatCryptoAmount(amount)
        }

        if (isPoints(amount)) {
            return this.formatPointsAmount(amount, options, locale)
        }

        const { formatter, value } = this.formatCurrencyInternal(
            amount,
            options,
            locale
        )

        // Format the amount.
        return formatter.format(parseFloat(value))
    }

    formatCurrencyWithOptionalFraction = (
        amount: Amount,
        options: FormatterOptions = {},
        locale?: string
    ) => {
        return formatCurrency(
            amount,
            { optionalFraction: true, ...options },
            locale
        )
    }

    formatCurrencyParts = (
        amount: Amount,
        options: FormatterOptions = {},
        locale?: string
    ): FormattedParts => {
        const { formatter, value } = this.formatCurrencyInternal(
            amount,
            options,
            locale
        )

        // Format the amount and return the string
        const parts = formatter.formatToParts(parseFloat(value))

        const formattedParts: FormattedParts = {
            sign: undefined,
            symbol: '',
            symbolPlacement: 'left',
            integer: '',
            decimal: undefined,
            fraction: undefined,
        }

        parts.forEach((item, index) => {
            if (
                index == 0 &&
                (item.type === 'plusSign' || item.type === 'minusSign')
            ) {
                formattedParts.sign = item.value
            } else if (item.type === 'currency') {
                formattedParts.symbol = item.value
                if (index > 1 || (index == 1 && !formattedParts.sign)) {
                    formattedParts.symbolPlacement = 'right'
                }
            } else if (item.type === 'decimal') {
                formattedParts.decimal = item.value
            } else if (item.type === 'fraction') {
                formattedParts.fraction = item.value
            } else {
                // Everything that is not the currency or the symbol goes to integer.
                formattedParts.integer = formattedParts.integer.concat(
                    item.value
                )
            }
        })

        return formattedParts
    }

    formatOptions = (currency = 'USD', locale?: string): FormatOptions => {
        // Format a dummy value (zero), so we can extract formatting options
        const formatter = this.getFormatter(
            { currency, locale, style: 'currency' },
            locale
        )
        const parts = formatter.formatToParts(0)
        const symbol = parts.find((x) => x.type === 'currency')
        const decimal = parts.find((x) => x.type === 'decimal')
        const fraction = parts.find((x) => x.type === 'fraction')

        // Return parsed options
        return {
            decimal: decimal ? decimal.value : undefined,
            fractionLength: fraction ? fraction.value.length : 0,
            symbol: symbol ? symbol.value : '',
            symbolPlacement: parts[0] === symbol ? 'left' : 'right',
        }
    }

    /**
     * Formats BTC amount as Satoshi.
     */
    bitcoinToSatoshi = (amount: Amount): string => {
        const value = numeral(amount.value).multiply(100000000).value() ?? 0
        const parts = formatCurrencyParts(
            { ...amount, value: value.toString() },
            {
                minimumFractionDigits: 2,
                style: 'currency',
                currency: 'BTC',
            }
        )
        const { decimal, integer } = parts
        // If there is no decimal part the '.' symbol is in the `decimal` var.
        const decm =
            decimal && decimal.replace('.', '').length > 0 ? decimal : ''
        return integer.concat(decm).concat(' Satoshi')
    }

    /**
     * Formats points amount as dollars.
     */
    pointsToDollars = (amount: Amount): string => {
        if (amount.currency !== 'PTS') return ''

        const value = numeral(amount.value).multiply(0.01).value() ?? 0
        const parts = formatCurrencyParts(
            { ...amount, value: value.toString() },
            {
                minimumFractionDigits: 2,
                style: 'currency',
                currency: 'USD',
            }
        )
        const { decimal, integer, symbol } = parts
        // If there is no decimal part the '.' symbol is in the `decimal` var.
        const decm =
            decimal && decimal.replace('.', '').length > 0 ? decimal : ''
        return `${symbol}${integer.concat(decm)}`
    }

    formatNumber = (value: number, locale?: string): string => {
        const options = { locale, style: 'decimal' }
        return this.getFormatter(options, locale).format(value)
    }

    private formatCurrencyInternal(
        amount: Amount,
        options: FormatterOptions = {},
        locale?: string
    ): { formatter: Intl.NumberFormat; value: string } {
        const { value = '0', currency = 'USD' } = amount
        const amountValue = parseFloat(value)
        const {
            optionalFraction = false,
            currencyHidden = false,
            ...restOptions
        } = options

        const fractionOpts: Pick<
            Intl.NumberFormatOptions,
            'minimumFractionDigits' | 'maximumFractionDigits'
        > = {}
        if (optionalFraction && Number.isInteger(amountValue)) {
            fractionOpts.minimumFractionDigits = 0
            fractionOpts.maximumFractionDigits = 0
        }

        // Based on the formatter config, generate a unique cache key
        const opts = {
            currency,
            locale,
            style: currencyHidden ? 'decimal' : 'currency',
            ...restOptions,
            ...fractionOpts,
        }
        return { formatter: this.getFormatter(opts, locale), value }
    }

    private getFormatter(
        options: Record<string, unknown>,
        locale?: string
    ): Intl.NumberFormat {
        const cacheKey = JSON.stringify(options)
        let formatter = this.cache.get(cacheKey)
        if (!formatter) {
            formatter = new Intl.NumberFormat(locale, options)
            this.cache.set(cacheKey, formatter)
        }
        return formatter
    }

    private formatPointsAmount(
        amount: Amount,
        {
            currencyHidden = false,
            longCurrencyName = false,
            ...rest
        }: FormatterOptions,
        locale?: string
    ): string {
        const { formatter, value } = this.formatCurrencyInternal(
            amount,
            {
                optionalFraction: true,
                currencyHidden: true, // We manually handle adding the "pts" currency below
                ...rest,
            },
            locale
        )
        const formattedValue = formatter.format(parseInt(value))
        const currency = longCurrencyName ? 'points' : 'pts'
        return currencyHidden ? formattedValue : `${formattedValue} ${currency}`
    }
})()

export interface FormatOptions {
    symbol: string
    symbolPlacement: 'left' | 'right'
    decimal?: string
    fractionLength: number
}

export interface FormatterOptions extends Intl.NumberFormatOptions {
    optionalFraction?: boolean
    currencyHidden?: boolean
    longCurrencyName?: boolean
}

export interface FormattedParts {
    sign?: string
    symbol: string
    symbolPlacement: 'left' | 'right'
    integer: string
    decimal?: string
    fraction?: string
}
